/**
 * Streaming Server Configuration
 * 2ECOMP Fleet Management
 *
 * Architecture:
 *   Camera -> [JT1078 TCP:10780] -> Java Gateway -> [FFmpeg: G.711A->AAC, video copy] -> [RTMP] -> ZLMediaKit -> [WS-FLV] -> Browser
 *
 * Java Gateway (glaciall/jtt1078-video-server) handles JT1078 protocol.
 * FFmpeg wrapper (/usr/local/bin/ffmpeg-audio) transcodes G.711A audio to AAC.
 * ZLMediaKit handles media distribution to browsers (modifyStamp=2, timestamp correction).
 * Stream key format: {SIM}-{channel} (e.g., 496079623792-1)
 *
 * Server URL (M7S_CONFIG.serverUrl) removed — use useCameraServer() hook instead.
 */

export const M7S_CONFIG = {
  // Stream check interval (ms)
  checkInterval: 2000,

  // Max attempts before showing error
  maxAttempts: 5,

  // Timeout waiting for stream to become active (ms)
  // Aligned with ZLMediaKit maxStreamWaitMS=20000
  streamWaitTimeout: 20000,

  // Primary protocol for browser playback
  // 'zlm-ws-flv' = ZLMediaKit WebSocket-FLV (recommended, timestamp correction)
  // 'webrtc' = M7S WebRTC (legacy, no timestamp correction)
  // 'flv' = M7S HTTP-FLV (legacy)
  preferredProtocol: 'zlm-ws-flv',
};

/**
 * Build stream URLs for different protocols
 * @param {string} baseUrl - Base URL from useCameraServer()
 * @param {string} imei - Device IMEI
 * @param {number} channel - Camera channel (1-8)
 * @returns {Object} URLs for different protocols
 */
export const getStreamUrls = (baseUrl, imei, channel = 1) => {
  const wssBase = baseUrl.replace('https://', 'wss://').replace('http://', 'ws://');
  const streamName = `${imei}-${channel}`;

  return {
    // === ZLMediaKit URLs (primary - timestamp corrected) ===
    // WS-FLV: ZLMediaKit auto-generates .live.flv endpoints
    zlmWsFlv: `${wssBase}/live/${streamName}.live.flv`,
    zlmHttpFlv: `${baseUrl}/live/${streamName}.live.flv`,

    // Playback via ZLMediaKit (same stream name as live — Java Gateway
    // pushes both live and playback data to live/{SIM}-{channel})
    zlmPlaybackWsFlv: `${wssBase}/live/${streamName}.live.flv`,
    zlmPlaybackHttpFlv: `${baseUrl}/live/${streamName}.live.flv`,

    // === ZLMediaKit WebRTC (WHEP) ===
    zlmWebrtc: `${baseUrl}/index/api/webrtc?app=live&stream=${streamName}&type=play`,

    // === M7S URLs (legacy/fallback) ===
    webrtc: `${baseUrl}/webrtc/play/live/${streamName}`,
    flv: `${baseUrl}/flv/live/${streamName}`,
    wsFlv: `${wssBase}/ws/live/${streamName}.flv`,
    hls: `${baseUrl}/hls/live/${streamName}/index.m3u8`,

    // M7S playback (legacy — same path as live)
    playbackWebrtc: `${baseUrl}/webrtc/play/live/${streamName}`,
    playbackFlv: `${baseUrl}/flv/live/${streamName}`,

    // Stream paths (internal)
    streamPath: `live/${streamName}`,
    playbackStreamPath: `live/${streamName}`,
  };
};

/**
 * Check if a stream is active on ZLMediaKit
 * Uses Nginx proxy endpoint /api/streams (injects secret server-side)
 * @param {string} baseUrl - Base URL from useCameraServer()
 */
export const isStreamActive = async (baseUrl, imei, channel = 1) => {
  try {
    const response = await fetch(`${baseUrl}/api/streams`);
    const data = await response.json();
    const streamName = `${imei}-${channel}`;
    return data.data?.some((s) => s.app === 'live' && s.stream === streamName) || false;
  } catch (error) {
    console.error('[ZLM] Error checking stream:', error);
    return false;
  }
};

/**
 * Check if a playback stream is active on ZLMediaKit
 * Note: Java Gateway publishes both live and playback to the same
 * stream name (live/{SIM}-{channel}), so we check the same path.
 * @param {string} baseUrl - Base URL from useCameraServer()
 */
export const isPlaybackStreamActive = async (baseUrl, imei, channel = 1) => {
  try {
    const response = await fetch(`${baseUrl}/api/streams`);
    const data = await response.json();
    const streamName = `${imei}-${channel}`;
    return data.data?.some((s) => s.app === 'live' && s.stream === streamName) || false;
  } catch (error) {
    console.error('[ZLM] Error checking playback stream:', error);
    return false;
  }
};

export default M7S_CONFIG;
